import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, tap, BehaviorSubject, catchError, of } from 'rxjs';
import { Router } from '@angular/router';
import { runInInjectionContext, Injector } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { 
  LoginStage1Request, 
  LoginStage1Response, 
  LoginStage2Request, 
  AuthResponse,
  GoogleLoginRequest,
  MicrosoftLoginRequest,
  RecoveryPasswordRequest,
  DefinePasswordRequest
} from '../models';


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private injector = inject(Injector);

  private readonly baseUrl = environment.discoveryApiUrl;

  // Estado da autenticação usando Angular Signals
  private _isAuthenticated = signal(false);
  private _currentUser = signal<AuthResponse | null>(null);

  // Observables para compatibilidade
  private _authState$ = new BehaviorSubject<boolean>(false);

  // Getters públicos
  public isAuthenticated$ = this._authState$.asObservable();
  public readonly isAuthenticated = this._isAuthenticated.asReadonly();
  public readonly currentUser = this._currentUser.asReadonly();

  constructor() {
    // Verifica se já existe um token válido no localStorage
    this.checkExistingAuth();
  }

  private checkExistingAuth(): void {
    const token = this.getToken();
    const userData = this.getUserData();
    
    if (token && userData && !this.isTokenExpired(token)) {
      this._isAuthenticated.set(true);
      this._currentUser.set(userData);
      this._authState$.next(true);
    } else {
      this.clearAuthData();
    }
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch (error) {
      return true; // Se não conseguir decodificar, considera expirado
    }
  }

  validateCredentials(credentials: LoginStage1Request): Observable<LoginStage1Response> {
    return this.http.post<number | { authType?: number; requiresTenant?: boolean }>(`${this.baseUrl}/_`, credentials).pipe(
      map((response: number | { authType?: number; requiresTenant?: boolean }) => {
        const requiresTenant = typeof response === 'object' && response.requiresTenant === true;
        const authType = typeof response === 'number' ? response : response.authType;
        if (requiresTenant || authType === 1) {
          return {
            loginType: 'quiron' as const,
            requiresTenant,
            sessionToken: undefined
          };
        }
        
        // Handle other response types in the future
        throw new Error('Tipo de login não suportado');
      })
    );
  }

  authenticateUser(request: LoginStage2Request): Observable<AuthResponse> {
    const endpoint = `${this.baseUrl}/_/${request.loginType}`;
    
    const payload: { email: string; password: string; tenantName?: string } = {
      email: request.email,
      password: request.password
    };
    if (request.tenantName) {
      payload['tenantName'] = request.tenantName;
    }

    return this.http.post<AuthResponse>(endpoint, payload).pipe(tap(response => this.completeLogin(response)));
  }

  authenticateGoogle(request: GoogleLoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/_/Google`, request).pipe(
      tap(response => this.completeLogin(response))
    );
  }

  authenticateMicrosoft(request: MicrosoftLoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/_/Microsoft`, request).pipe(
      tap(response => this.completeLogin(response))
    );
  }


  private completeLogin(response: AuthResponse): void {
    if (response.requiresTenant) {
      return;
    }

    if (!response.token) {
      throw new Error('Resposta de autenticação inválida');
    }

    this.setAuthData(response);
    this._isAuthenticated.set(true);
    this._currentUser.set(response);
    this._authState$.next(true);

    setTimeout(() => {
      runInInjectionContext(this.injector, () => {
        import('../../../services/admin/customer-selection.service').then(module => {
          const service = this.injector.get(module.CustomerSelectionService);
          service.loadAvailableCustomers(true).subscribe();
        });
      });
    }, 0);
  }

  // Métodos públicos para gerenciamento de autenticação
  isAuthenticatedSync(): boolean {
    return this._isAuthenticated();
  }

  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  getUserData(): AuthResponse | null {
    const userData = localStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
  }

  private setAuthData(authResponse: AuthResponse): void {
    if (authResponse.token) {
      localStorage.setItem('auth_token', authResponse.token);
    }
    localStorage.setItem('user_data', JSON.stringify(authResponse));
  }

  private clearAuthData(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    this._isAuthenticated.set(false);
    this._currentUser.set(null);
    this._authState$.next(false);
  }

  logout(options: { navigate?: boolean } = { navigate: true }): void {
    const token = this.getToken();

    if (token && !this.isTokenExpired(token)) {
      this.http.post(`${this.baseUrl}/Session/Logout`, {}).pipe(
        catchError(() => of(null))
      ).subscribe();
    }

    this.finishLogout(options);
  }

  private finishLogout(options: { navigate?: boolean } = { navigate: true }): void {
    this.clearAuthData();
    // Limpa dados do customer selection de forma assíncrona para evitar circularidade
    setTimeout(() => {
      runInInjectionContext(this.injector, () => {
        import('../../../services/admin/customer-selection.service').then(module => {
          const service = this.injector.get(module.CustomerSelectionService);
          service.clearAllData();
        });
      });
    }, 0);
    if (options.navigate !== false) {
      this.router.navigate(['/auth/login']);
    }
  }

  // Método para verificar se o usuário tem uma sessão válida
  validateSession(): Observable<boolean> {
    const token = this.getToken();
    
    if (!token || this.isTokenExpired(token)) {
      this.logout();
      return new Observable(observer => {
        observer.next(false);
        observer.complete();
      });
    }

    // Aqui você pode implementar uma chamada ao backend para validar a sessão
    // Por enquanto, apenas verifica se o token não está expirado
    return new Observable(observer => {
      observer.next(true);
      observer.complete();
    });
  }

  // Método para refresh token (implementar quando necessário)
  refreshToken(): Observable<AuthResponse> {
    // TODO: Implementar refresh token com o backend
    throw new Error('Refresh token não implementado ainda');
  }

  // Métodos para recuperação de senha
  recoverPassword(data: RecoveryPasswordRequest): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/User/RecoveryPassword`,
      { email: data.email, tenantName: data.tenantName },
      {
        headers: new HttpHeaders({
          'X-Tenant-Name': data.tenantName,
        }),
      }
    );
  }

  definePassword(data: DefinePasswordRequest): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/User/DefinePassword`,
      {
        password: data.password,
        tokenId: data.tokenId,
        tenantName: data.tenantName
      },
      {
        headers: new HttpHeaders({
          'X-Tenant-Name': data.tenantName,
        }),
      }
    );
  }

}
